import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { usePregnancyStore } from '@/store/pregnancyStore';
import { useLocale } from '@parentpath/i18n';
import {
  calculateBabyAgeInWeeks,
  getCurrentPhase,
  getPhaseDisplayRange,
  calculatePhaseProgress,
} from '../utils/phaseCalculation';
import type { BabyDevelopmentPhase } from '../types';

export interface UseBabyPhaseResult {
  ageWeeks: number;
  phaseNumber: number;
  phaseDisplayRange: string;
  phaseProgress: number;
  phase: BabyDevelopmentPhase | null;
  isLoading: boolean;
  error: Error | null;
}

export function useBabyPhase(): UseBabyPhaseResult | null {
  const { lastCompletedPregnancy, appMode } = usePregnancyStore();
  const { locale } = useLocale();

  // Calculate age and phase from birth date
  const phaseInfo = useMemo(() => {
    if (!lastCompletedPregnancy?.actual_birth_date) return null;

    const birthDate = new Date(lastCompletedPregnancy.actual_birth_date);
    // TODO: Add premature_weeks support when available in pregnancy data
    const ageWeeks = calculateBabyAgeInWeeks(birthDate, 0);
    const phaseNumber = getCurrentPhase(ageWeeks);
    const phaseDisplayRange = getPhaseDisplayRange(phaseNumber);
    const phaseProgress = calculatePhaseProgress(ageWeeks, phaseNumber);

    return {
      ageWeeks,
      phaseNumber,
      phaseDisplayRange,
      phaseProgress,
    };
  }, [lastCompletedPregnancy?.actual_birth_date]);

  // Fetch phase content from database
  const { data: phase, isLoading, error } = useQuery({
    queryKey: ['baby-development-phase', phaseInfo?.phaseNumber, locale],
    queryFn: async () => {
      if (!phaseInfo) return null;

      const { data, error } = await supabase
        .from('baby_development_phases')
        .select('*')
        .eq('phase_number', phaseInfo.phaseNumber)
        .eq('locale', locale)
        .single();

      if (error) throw error;
      return data as BabyDevelopmentPhase;
    },
    enabled: !!phaseInfo && appMode === 'baby',
  });

  if (!phaseInfo) return null;

  return {
    ...phaseInfo,
    phase: phase ?? null,
    isLoading,
    error: error as Error | null,
  };
}
